/**
 * useVersionGate — polls build.json and prompts reload when a new deploy lands.
 *
 * DEF-156 (2026-08-04): clients keep stale JS after a deploy because
 * CloudFront serves cached bundles until invalidation propagates. This hook
 * detects the mismatch and prompts the user to reload.
 *
 * Pattern: standard SPA version gate.
 *   - On mount + every 60s: fetch /build.json (no-cache)
 *   - Compare response.sha against the build-time REACT_APP_BUILD_SHA
 *   - If they differ: show a non-blocking toast/banner
 *   - User clicks "Reload" → window.location.reload()
 *
 * Does NOT force-reload automatically (would lose unsaved work in the builder).
 * The prompt is dismissible but re-appears on the next poll cycle.
 */

import { useEffect, useRef, useCallback } from 'react';

const BUILD_SHA = process.env.REACT_APP_BUILD_SHA || '';
const POLL_INTERVAL_MS = 60_000; // 60 seconds

export default function useVersionGate({ onNewVersion }) {
  const dismissedRef = useRef(false);
  const intervalRef = useRef(null);

  const check = useCallback(async () => {
    if (!BUILD_SHA) return; // dev mode — no build sha available
    try {
      const res = await fetch('/build.json', { cache: 'no-store' });
      if (!res.ok) return;
      const data = await res.json();
      const remoteSha = data.sha || data.shortSha || '';
      if (remoteSha && remoteSha !== BUILD_SHA && !remoteSha.startsWith(BUILD_SHA)) {
        // New version deployed — notify consumer
        if (!dismissedRef.current && typeof onNewVersion === 'function') {
          onNewVersion({
            currentSha: BUILD_SHA.slice(0, 7),
            newSha: remoteSha.slice(0, 7),
            reload: () => window.location.reload(),
            dismiss: () => { dismissedRef.current = true; },
          });
        }
      }
    } catch {
      // Network error — skip this cycle
    }
  }, [onNewVersion]);

  useEffect(() => {
    // Initial check after a short delay (let the app settle)
    const initialTimer = setTimeout(check, 5000);
    // Recurring poll
    intervalRef.current = setInterval(() => {
      dismissedRef.current = false; // re-surface on each cycle
      check();
    }, POLL_INTERVAL_MS);

    return () => {
      clearTimeout(initialTimer);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [check]);
}
